/**
 * "A new version is ready" — the bar, and nothing about when it appears.
 *
 * Every rule (the silent first seconds, Later applying on the next hide, the
 * reload guard) is `lib/sw-update.ts`'s and is tested there. This draws what
 * that module says and hands the two clicks back.
 *
 * ## In the flow, not over it
 *
 * In both shells it is the first child of the column that already holds the
 * acting-as banner, so it **pushes the app down** by one thin row rather than
 * covering anything. Overlaid, it would sit on the layout tabs on a desktop and
 * on the top of the phone shell — controls somebody may be reaching for at the
 * moment it appears. A row that arrives and leaves is a small reflow; the chart
 * resizes with its container, as it does for a divider drag.
 *
 * The sign-in screen is the exception, `overlay`: it is a centred card with
 * nothing at the top edge to cover, and it is laid out as a grid that a new row
 * would re-centre. There the bar inherits that screen's own theme variables —
 * the screen sits outside `ThemeProvider` and carries its own (see `SignIn.tsx`)
 * — so it needs none of its own, and must not carry any: in the shell it has to
 * follow the user's theme.
 *
 * ## Accessible as a status, not an alert
 *
 * `role="status"` is polite: an update can wait for a screen reader to finish
 * its sentence. The region is always mounted, empty when hidden, because a live
 * region inserted together with its text is not reliably announced.
 */

import { useSyncExternalStore } from "react";

import { currentUpdater, type Updater } from "../lib/sw-update";

export function UpdateBar({
  overlay = false,
  updater = currentUpdater(),
}: {
  /** Float over the top edge instead of taking a row — the sign-in screen. */
  overlay?: boolean;
  updater?: Updater;
}) {
  const state = useSyncExternalStore(updater.subscribe, updater.getSnapshot);
  const shown = state !== "hidden";
  const applying = state === "applying";

  return (
    <div role="status" style={overlay ? (shown ? OVERLAY : OUT_OF_FLOW) : shown ? BAR : undefined}>
      {shown && (
        <>
          <span style={MESSAGE}>
            <span aria-hidden="true">⟳</span> A new version is ready.
          </span>
          <button
            type="button"
            onClick={() => updater.reload()}
            disabled={applying}
            style={RELOAD}
          >
            {applying ? "Reloading…" : "Reload"}
          </button>
          <button
            type="button"
            onClick={() => updater.later()}
            disabled={applying}
            style={LATER}
            title="Hide this — the update is applied the next time you leave the tab"
          >
            Later <span aria-hidden="true">×</span>
          </button>
        </>
      )}
    </div>
  );
}

const BAR: React.CSSProperties = {
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  // A last resort on the narrowest phone: Later drops to a second line rather
  // than the row forcing a horizontal scroll.
  flexWrap: "wrap",
  gap: "0.25rem 0.7rem",
  padding: "0.3rem 0.8rem",
  // Below the status bar on an installed iPhone app, where the page runs under
  // it (`viewport-fit=cover` + `black-translucent`); zero everywhere else.
  paddingTop: "calc(0.3rem + env(safe-area-inset-top, 0px))",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  borderBottom: "1px solid var(--qf-border)",
  fontSize: "0.78rem",
  lineHeight: 1.3,
};

const OVERLAY: React.CSSProperties = {
  ...BAR,
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1,
};

/**
 * The empty region over the sign-in screen. Out of flow even when empty: that
 * screen is a grid, and an empty item in flow is a second row that moves the
 * card off centre.
 */
const OUT_OF_FLOW: React.CSSProperties = { position: "absolute", top: 0, left: 0 };

const MESSAGE: React.CSSProperties = { minWidth: 0 };

const RELOAD: React.CSSProperties = {
  padding: "0.15rem 0.65rem",
  background: "var(--qf-accent)",
  color: "var(--qf-background)",
  border: "none",
  borderRadius: 4,
  font: "inherit",
  fontWeight: 600,
  cursor: "pointer",
};

const LATER: React.CSSProperties = {
  // Pushed to the far end, as in the approved mock: the two choices are not
  // equals, and putting them side by side would read as though they were.
  marginLeft: "auto",
  padding: "0.15rem 0.3rem",
  background: "transparent",
  color: "var(--qf-text-secondary)",
  border: "none",
  font: "inherit",
  cursor: "pointer",
};
